/**
 * 生値のうち、Excel / Google スプレッドシートで数式として解釈されるものを無効化する。
 *
 * 先頭が `=` `+` `-` `@` と改行系（タブ・CR・LF）の値が対象で、シングルクォートを前置する。
 * 改行系を含めるのは、先頭の空白扱いの文字が読み飛ばされて後続の `=` が数式になるため
 * （OWASP CSV Injection）。
 *
 * **囲み（シリアライズ）は行わない。** 誰が囲むかは出力経路で違うため分けている。
 * - 自前で組み立てる CSV → {@link escapeCsvCell}（この関数＋ダブルクォート囲み）
 * - react-csv 経由 → `escapeForCSV`（この関数＋`"` の二重化。囲みは react-csv 側）
 */
export const neutralizeCsvFormula = (value: string | number | null | undefined): string => {
  const raw = String(value ?? "");
  return /^[=+\-@\t\r\n]/.test(raw) ? `'${raw}` : raw;
};

/**
 * CSV のセル1つ分を RFC 4180 のダブルクォート囲みで出す。
 *
 * 店舗名・ブランド名・チケットタイトルのように利用者が自由入力できる値がそのまま入るので、
 * 自前で CSV を組み立てるときは必ずこの関数を通す。
 */
export const escapeCsvCell = (value: string | number | null | undefined): string =>
  `"${neutralizeCsvFormula(value).replace(/"/g, '""')}"`;

/**
 * CSV 生成が失敗したときの理由を、利用者が次の行動を選べる粒度の文言に落とす。
 *
 * 「押しても何も起きない」状態を避けるのが目的なので、原因を特定できない場合も
 * 必ず何らかの文言を返す（空文字は返さない）。
 *
 * 想定する失敗:
 * - レスポンスが返らない（タイムアウト・通信断）
 * - 件数過多でサーバーが落ちる / 応答できない（5xx）
 * - 認証切れ（401 / 403）
 */
export const getCsvGenerationErrorMessage = (error: unknown): string => {
  const e = error as {
    response?: { status?: number; data?: { message?: string } };
  } | null;

  const status = e?.response?.status;
  // API が返した文言があれば、そちらのほうが具体的なので優先する
  const serverMessage = e?.response?.data?.message;

  if (!e?.response) {
    // サーバーが落ちた場合も CORS プリフライトの時点で失敗し、ここに入る。
    // 本番の heap OOM（Cloud Run のコンテナ異常終了）もこの経路になるため、
    // 件数過多の可能性まで含めて案内する。
    return "通信が中断されました。対象件数が多すぎて時間内に生成しきれなかった可能性があります。条件を絞ってから再実行してください。";
  }
  if (status === 503 || status === 504) {
    return "サーバーが応答できませんでした。対象件数が多すぎる可能性があります。絞り込んでから再実行してください。";
  }
  if (status === 413 || status === 429) {
    return serverMessage || "リクエストが大きすぎるか、実行回数が多すぎます。時間をおいて再実行してください。";
  }
  if (status !== undefined && status >= 500) {
    return serverMessage || "サーバーでエラーが発生しました。時間をおいて再実行してください。";
  }
  if (status === 401 || status === 403) {
    return serverMessage || "権限がありません。再ログインしてから実行してください。";
  }
  if (status !== undefined && status >= 400) {
    return serverMessage || "リクエストが受け付けられませんでした。";
  }
  return serverMessage || "CSVの生成に失敗しました。時間をおいて再実行してください。";
};

/**
 * react-csv（`CSVLink`）に渡す値のエスケープ。
 *
 * react-csv の `joiner` は値をダブルクォートで囲むだけで中身をエスケープしないため、
 * `"` の二重化は呼び出し側の責務。囲みは react-csv が行うのでここでは付けない
 * （自前で組み立てる CSV は {@link escapeCsvCell} を使う）。
 */
export const escapeForCSV = (original: string | null | undefined, ignoreNewLine: boolean = false): string => {
  if (!original) {
    return "";
  }
  const formulaSafe = neutralizeCsvFormula(original).replace(/"/g, '""');
  return ignoreNewLine ? formulaSafe.replace(/\n/g, " ") : formulaSafe;
};

/**
 * react-csv に渡す行データを、出力境界でまとめてエスケープする。
 *
 * 列ごとに {@link escapeForCSV} を呼ぶ方式は「どの列が自由入力か」の判断に依存するため、
 * 検証されていると思い込んでいた列（URL・電話番号など）を取りこぼす。
 * 実際 `reservationUrl` や `brandUrl` は保存時に検証されず、電話番号も任意文字列を受け付けるため、
 * `=HYPERLINK(...)` がそのまま CSV に載っていた（#1559）。
 * 判断を挟まずここで全列を通す。
 *
 * 文字列と文字列配列だけを対象にする（数値・真偽値・null はそのまま。
 * ネストしたオブジェクトは header の key が参照しないため触らない）。
 */
export const escapeCsvRows = <T extends Record<string, unknown>>(rows: T[], ignoreNewLine = false): T[] =>
  rows.map(
    (row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => {
          if (typeof value === "string") return [key, escapeForCSV(value, ignoreNewLine)];
          if (Array.isArray(value)) {
            return [key, value.map((item) => (typeof item === "string" ? escapeForCSV(item, ignoreNewLine) : item))];
          }
          return [key, value];
        }),
      ) as T,
  );
